import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';
import { useAuthState } from '../hooks/useAuthState';
import { FiMenu, FiX, FiExternalLink, FiHome, FiInfo, FiMail, FiUser, FiCalendar, FiLogOut, FiSearch, FiSmartphone } from 'react-icons/fi';
import usePWAInstall from '../hooks/usePWAInstall';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Enhanced Navbar Component
 * 
 * A professional, responsive navigation bar with strategic PWA installation prompts.
 * Key features:
 * - Mobile-only PWA install icon (subtle integration)
 * - Automatic hide after installation
 * - No desktop PWA prompts (as per requirements)
 * - Smooth animations and professional UI
 * - User authentication state management
 * 
 * @param {Object} props - Component props
 * @param {Object} props.loadingBarRef - Reference to the loading bar component
 * @returns {JSX.Element} - Rendered navbar component
 */
function Navbar({ loadingBarRef }) {
  // Core state management
  const [user, loading] = useAuthState(auth);
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  // Enhanced PWA install hook with mobile detection
  const { isInstallable, isInstalled, isMobile,  promptInstall } = usePWAInstall();
  
  // Handle scroll events to update navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Toggle mobile menu state
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Implement search functionality here
      console.log('Searching for:', searchQuery);
      // Reset search after submission
      setSearchVisible(false);
    }
  };

  // Handle navigation with loading bar animation
  const handleNavigation = (path) => {
    if (loadingBarRef?.current) {
      loadingBarRef.current.continuousStart();
      setTimeout(() => {
        loadingBarRef.current.complete();
      }, 500);
    }
    setIsOpen(false);
    setSearchVisible(false);
  };

  /**
   * Handle PWA installation with iOS-specific instructions
   * Only triggered for mobile users
   */
  const handlePWAInstall = async () => {
    if (!isMobile) return; // Safety check - should not happen
    
    try {
      const result = await promptInstall();
      
      // Handle iOS-specific installation instructions
      if (result && result.type === 'ios') {
        setShowIOSInstructions(true);
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }
  };

  // Animation variants
  const menuVariants = {
    closed: {
      opacity: 0,
      y: -10,
      transition: { duration: 0.2, ease: 'easeInOut' }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.2, ease: 'easeInOut' }
    }
  };

  const searchVariants = {
    hidden: { 
      opacity: 0, 
      width: 0,
      transition: { duration: 0.2 }
    },
    visible: { 
      opacity: 1, 
      width: '100%',
      transition: { duration: 0.2 }
    }
  };

  // Navigation links configuration
  const navLinks = [
    { path: '/', label: 'Home', icon: FiHome },
    { path: '/about', label: 'About', icon: FiInfo },
    { path: '/contact', label: 'Contact', icon: FiMail },
  ];

  return (
    <>
      <nav 
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled 
            ? 'bg-white shadow-lg py-2' 
            : 'bg-white/90 backdrop-blur-sm py-3'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link 
              to="/" 
              onClick={() => handleNavigation('/')} 
              className="flex items-center space-x-2 z-10"
            >
              <span className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-3 py-1.5 rounded-lg text-sm font-bold">
                YouthXConnect Events Portal
              </span>
              <span className="hidden sm:block font-semibold text-gray-800 text-lg">
                
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavigation(link.path)}
                  className="px-3 py-2 text-gray-700 hover:text-indigo-600 rounded-md transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
              
              <Link
                to={user ? '/account' : '/register'}
                onClick={() => handleNavigation(user ? '/account' : '/register')}
                className="ml-2 px-4 py-2 text-indigo-600 border border-indigo-200 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
              >
                {user ? 'My Account' : 'Setup YxC Account to Register'}
              </Link>
            </div>

            {/* Mobile Navigation Controls */}
            <div className="flex items-center md:hidden space-x-2">
              {/* PWA Install Icon - ONLY for mobile users, ONLY if installable and not installed */}
              {isMobile && isInstallable && !isInstalled && (
                <button
                  onClick={handlePWAInstall}
                  className="p-2 rounded-full hover:bg-indigo-50 transition-colors group relative"
                  aria-label="Install App"
                  title="Install App"
                >
                  <FiSmartphone className="w-5 h-5 text-gray-600 group-hover:text-indigo-600 transition-colors" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-500 rounded-full animate-pulse"></span>
                </button>
              )}

              {/* Search Toggle (Mobile) */}
              <button
                onClick={() => setSearchVisible(!searchVisible)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Search"
              >
                <FiSearch className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Menu Toggle */}
              <button
                onClick={toggleMenu}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Menu"
              >
                {isOpen ? (
                  <FiX className="w-6 h-6 text-gray-600" />
                ) : (
                  <FiMenu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            </div>

            {/* Desktop Right Actions - NO PWA install button */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Search Toggle (Desktop) */}
              <button
                onClick={() => setSearchVisible(!searchVisible)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Search"
              >
                <FiSearch className="w-5 h-5 text-gray-600" />
              </button>
              
              {/* Main Site Link */}
              <a
                href="https://youthxconnect.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-indigo-600 border border-gray-200 rounded-lg hover:border-indigo-200 hover:bg-indigo-50 transition-all duration-200"
              >
                Main Site <FiExternalLink className="ml-1.5" size={14} />
              </a>
            </div>
          </div>
          
          {/* Search Bar - Animated */}
          <AnimatePresence>
            {searchVisible && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={searchVariants}
                className="absolute left-0 top-full w-full bg-white shadow-md p-4 border-t border-gray-100"
              >
                <form onSubmit={handleSearchSubmit} className="relative">
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    autoFocus
                  />
                  <FiSearch className="absolute left-3 top-3 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => setSearchVisible(false)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  >
                    <FiX />
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                onClick={() => setIsOpen(false)}
              />

              {/* Menu Content */}
              <motion.div
                initial="closed"
                animate="open"
                exit="closed"
                variants={menuVariants}
                className="absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-xl z-50 max-h-[80vh] overflow-y-auto"
              >
                <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 space-y-6">
                  {/* User Section */}
                  <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-indigo-100 rounded-full">
                      <FiUser className="text-indigo-600" size={20} />
                    </div>
                    <div className="ml-3">
                      {loading ? (
                        <div className="h-5 w-24 bg-gray-200 animate-pulse rounded"></div>
                      ) : user ? (
                        <>
                          <p className="text-sm font-medium text-gray-900">{user.displayName || user.email}</p>
                          <Link
                            to="/account"
                            onClick={() => handleNavigation('/account')}
                            className="text-xs text-indigo-600 font-medium"
                          >
                            View Account
                          </Link>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 text-red-500">CREATE ACCOUNT TO REGISTER FOR EVENTS</p>
                          <Link
                            to="/login"
                            onClick={() => handleNavigation('/login')}
                            className="text-xs text-indigo-600 font-medium"
                          >
                            Login to existing account
                          </Link> <br></br><br></br>
                          <Link
                            to="/register"
                            onClick={() => handleNavigation('/login')}
                            className="text-xs text-indigo-600 font-medium"
                          >
                            Create new account
                          </Link>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Navigation Links */}
                  <div className="grid gap-1">
                    {navLinks.map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => handleNavigation(link.path)}
                        className="flex items-center px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-lg transition-all duration-200"
                      >
                        <link.icon className="w-5 h-5 mr-3" />
                        <span>{link.label}</span>
                      </Link>
                    ))}
                    
                    {/* Additional Mobile Links */}
                    <Link
                      to="/events"
                      onClick={() => handleNavigation('/events')}
                      className="flex items-center px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-lg transition-all duration-200"
                    >
                      <FiCalendar className="w-5 h-5 mr-3" />
                      <span>All Events</span>
                    </Link>
                    
                    {user && (
                      <button
                        onClick={() => {
                          auth.signOut();
                          setIsOpen(false);
                        }}
                        className="flex items-center px-4 py-3 text-red-600 hover:bg-red-50/50 rounded-lg transition-all duration-200 w-full text-left"
                      >
                        <FiLogOut className="w-5 h-5 mr-3" />
                        <span>Sign Out</span>
                      </button>
                    )}
                  </div>
                  
                  {/* External Links */}
                  <div className="pt-4 border-t border-gray-100">
                    <a
                      href="https://youthxconnect.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-lg transition-all duration-200"
                    >
                      <FiExternalLink className="w-5 h-5 mr-3" />
                      <span>YouthXConnect Main Site</span>
                    </a>

                    {/* PWA Install Option in Mobile Menu - ONLY if installable */}
                    {isMobile && isInstallable && !isInstalled && (
                      <button
                        onClick={() => {
                          handlePWAInstall();
                          setIsOpen(false);
                        }}
                        className="flex items-center px-4 py-3 text-indigo-600 hover:bg-indigo-50/50 rounded-lg transition-all duration-200 w-full text-left"
                      >
                        <FiSmartphone className="w-5 h-5 mr-3" />
                        <span>Install App</span>
                        <span className="ml-auto text-xs bg-indigo-100 text-indigo-600 px-2 py-1 rounded">Recommended</span>
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>

      {/* iOS Installation Instructions Modal */}
      <AnimatePresence>
        {showIOSInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Install YxC Events</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p className="flex items-center">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3">1</span>
                  Tap the Share button at the bottom of Safari
                </p>
                <p className="flex items-center">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3">2</span>
                  Scroll down and tap "Add to Home Screen"
                </p>
                <p className="flex items-center">
                  <span className="bg-indigo-100 text-indigo-600 rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3">3</span>
                  Tap "Add" to install the app
                </p>
              </div>
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="w-full mt-6 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Got it!
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Spacer to prevent content from hiding under fixed navbar */}
      <div className={`h-16 ${scrolled ? 'md:h-16' : 'md:h-20'}`}></div>
    </>
  );
}

export default Navbar;
